import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CdkDragEnd } from '@angular/cdk/drag-drop';
import { AlertAction, AlertActionEvent, AlertItem, AlertType } from './alert-banner.model';

/** Default Font Awesome icon per alert type. Override per-item via AlertItem.icon. */
const DEFAULT_ICON_MAP: Record<AlertType, string> = {
  info: 'fas fa-circle-info',
  warning: 'fas fa-triangle-exclamation',
  success: 'fas fa-circle-check',
  error: 'fas fa-circle-exclamation',
  critical: 'fas fa-circle-exclamation',
  bell: 'fas fa-bell',
};

@Component({
  selector: 'app-alert-banner',
  templateUrl: './alert-banner.component.html',
  styleUrls: ['./alert-banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AlertBannerComponent implements OnChanges {
  /** The alerts to render. Owned by the parent — this component never fetches data itself. */
  @Input() alerts: AlertItem[] = [];

  /**
   * 'carousel' = one alert at a time with prev/next + "n / total" (image 1).
   * 'list'     = all alerts stacked as cards, no pagination chrome (image 2 repeated).
   */
  @Input() layout: 'carousel' | 'list' = 'carousel';

  /** Show the overall widget close (✕) button — carousel layout only. */
  @Input() showClose = true;

  /** Optional heading shown above the whole widget (e.g. "Notifications"). */
  @Input() heading: string | null = null;

  /**
   * Shrink the banner in from the left — e.g. to clear a fixed sidebar/rail
   * that overlaps it. Accepts a number (treated as px) or any CSS length
   * string, e.g. `insetLeft="240"` or `insetLeft="'15rem'"`.
   */
  @Input() insetLeft: string | number = 0;

  /** Same as insetLeft, but shrinks in from the right instead. */
  @Input() insetRight: string | number = 0;

  /**
   * How the banner is taken out of (or kept in) normal document flow:
   * - 'static'   default — sits inline wherever you place it in the template.
   * - 'relative' inline, but can be nudged/z-indexed without affecting layout.
   * - 'sticky'   sticks to `placement` edge of its scroll container once reached.
   * - 'fixed'    pinned to `placement` edge of the viewport (survives scrolling).
   * - 'absolute' pinned to `placement` edge of the nearest positioned ancestor —
   *              wrap the host in a `position: relative` container to scope it.
   */
  @Input() position: 'static' | 'relative' | 'sticky' | 'fixed' | 'absolute' = 'static';

  /** Which edge to anchor to when `position` is 'sticky' | 'fixed' | 'absolute'. */
  @Input() placement: 'top' | 'bottom' = 'top';

  /** Distance from that edge. Number = px, or any CSS length string. */
  @Input() edgeOffset: string | number = 0;

  /** Stacking order when taken out of flow (fixed/absolute/sticky). */
  @Input() zIndex: string | number = 1000;

  /**
   * Lets the user drag the banner around at runtime (via Angular CDK's
   * cdkDrag). Only the grip handle in the header is draggable, not the
   * whole card — so it never fights with clicking buttons or actions.
   * Makes the most sense combined with `position="fixed"` or `"absolute"`
   * so the drag isn't fighting document flow.
   */
  @Input() draggable = false;

  /**
   * Optional drag boundary — an element (or CSS selector resolved against
   * the document) the banner can't be dragged outside of. Passed straight
   * through to cdkDragBoundary. Leave unset to allow free dragging.
   */
  @Input() dragBoundary: string | HTMLElement | undefined;

  /** Restrict dragging to one axis, or leave 'both' for free movement. */
  @Input() dragAxis: 'both' | 'x' | 'y' = 'both';

  /** Emitted with the final { x, y } pixel offset once a drag ends. */
  @Output() dragEnded = new EventEmitter<{ x: number; y: number }>();

  /** Emitted whenever any action button is clicked, regardless of whether it removes the alert. */
  @Output() actionClicked = new EventEmitter<AlertActionEvent>();

  /** Emitted when an alert is removed — either via a removeAlertOnClick action or the per-item dismiss (✕). */
  @Output() alertRemoved = new EventEmitter<AlertItem>();

  /** Emitted when the whole widget's close (✕) is clicked (carousel layout). */
  @Output() closed = new EventEmitter<void>();

  currentIndex = 0;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['alerts']) {
      // Keep currentIndex in range whenever the parent pushes a new array
      // (e.g. backend polling adds/removes alerts).
      const max = Math.max(this.alerts.length - 1, 0);
      if (this.currentIndex > max) {
        this.currentIndex = max;
      }
    }
  }

  get total(): number {
    return this.alerts.length;
  }

  get currentAlert(): AlertItem | null {
    return this.alerts[this.currentIndex] ?? null;
  }

  trackById = (_: number, alert: AlertItem): string | number => alert.id;

  private toCssLength(value: string | number): string {
    return typeof value === 'number' ? `${value}px` : value;
  }

  get marginLeft(): string {
    return this.toCssLength(this.insetLeft);
  }

  get marginRight(): string {
    return this.toCssLength(this.insetRight);
  }

  /**
   * Style object for the outer wrapper. When `position` is 'static' the
   * insets act as normal left/right margins. Otherwise the element is taken
   * out of flow and the SAME insets become `left`/`right` coordinates
   * (so it still shrinks in from either side — e.g. to clear a fixed
   * sidebar — while pinned to the viewport/ancestor/scroll-container).
   */
  get wrapperStyles(): Record<string, string> {
    if (this.position === 'static') {
      return {
        'margin-left': this.marginLeft,
        'margin-right': this.marginRight,
      };
    }

    return {
      position: this.position,
      left: this.marginLeft,
      right: this.marginRight,
      [this.placement]: this.toCssLength(this.edgeOffset),
      'z-index': String(this.zIndex),
    };
  }

  prev(): void {
    if (this.currentIndex > 0) {
      this.currentIndex--;
    }
  }

  next(): void {
    if (this.currentIndex < this.total - 1) {
      this.currentIndex++;
    }
  }

  iconFor(alert: AlertItem): string {
    return alert.icon || DEFAULT_ICON_MAP[alert.type] || DEFAULT_ICON_MAP.info;
  }

  onAction(alert: AlertItem, action: AlertAction): void {
    if (action.disabled) {
      return;
    }
    const index = this.alerts.findIndex((a) => a.id === alert.id);
    this.actionClicked.emit({ alert, action, index });

    if (action.removeAlertOnClick) {
      this.removeAlert(alert);
    }
    // If removeAlertOnClick is false/undefined, the alert stays put —
    // it's on the parent to update alert.tag/state (e.g. flip to
    // "ACKNOWLEDGED") by passing a new `alerts` array back in.
  }

  onDismiss(alert: AlertItem): void {
    this.removeAlert(alert);
  }

  close(): void {
    this.closed.emit();
  }

  /** Maps our friendly `dragAxis` input to what cdkDragLockAxis expects (undefined = free). */
  get dragLockAxis(): 'x' | 'y' | undefined {
    return this.dragAxis === 'both' ? undefined : this.dragAxis;
  }

  onDragEnded(event: CdkDragEnd): void {
    const { x, y } = event.source.getFreeDragPosition();
    this.dragEnded.emit({ x, y });
  }

  private removeAlert(alert: AlertItem): void {
    const removedIndex = this.alerts.findIndex((a) => a.id === alert.id);
    this.alerts = this.alerts.filter((a) => a.id !== alert.id);
    this.alertRemoved.emit(alert);

    if (removedIndex !== -1 && removedIndex <= this.currentIndex && this.currentIndex > 0) {
      this.currentIndex--;
    } else {
      const max = Math.max(this.alerts.length - 1, 0);
      if (this.currentIndex > max) {
        this.currentIndex = max;
      }
    }
  }
}